import React, { ReactNode, useCallback, useContext } from 'react';
import classNames from 'classnames';
import { Button } from 'ncoded-component-library';
import {
  DataRoomShareableLink,
  DataRoomShareableLinkRequest,
} from 'models/ShareableLink';
import useMatchMedia from 'hooks/useMatchMedia';
import { getUserFullName } from 'utils';
import { useTranslation } from 'react-i18next';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import EditShareableLinkContext from 'router/subrouters/DataRoom/providers/EditShareableLink/EditShareableLink.context';

import './WaitingListTableWrapper.styles.scss';
import './WaitingListTableWrapper.styles.responsive.scss';

type WaitingListTableWrapperProps = {
  className?: string;
  actionsColumn?: ReactNode;
  items: DataRoomShareableLinkRequest[];
  getActions: (item: DataRoomShareableLinkRequest) => ReactNode;
};

const WaitingListTableWrapper: React.FC<WaitingListTableWrapperProps> = (
  props,
) => {
  const { className, actionsColumn, items, getActions } = props;

  const classes = classNames('waiting-list-table-wrapper', className);

  const { t } = useTranslation();

  const isTablet = useMatchMedia('isTablet');

  const { doesntHaveAccess } = useContext(DataRoomContext);
  const { openEditShareableLink } = useContext(EditShareableLinkContext);

  const renderName = useCallback(
    (firstName: string, lastName: string, email: string) => (
      <div className="name">
        <span>{getUserFullName(firstName, lastName)}</span>
        {email && <p className="email">{email}</p>}
      </div>
    ),
    [],
  );

  const renderShareableLink = useCallback(
    (dataRoomShareableLink: DataRoomShareableLink) => (
      <Button
        variant="link"
        disabled={doesntHaveAccess}
        onClick={() => {
          if (doesntHaveAccess) return;
          openEditShareableLink(dataRoomShareableLink);
        }}
      >
        {dataRoomShareableLink.name}
      </Button>
    ),
    [doesntHaveAccess, openEditShareableLink],
  );

  return isTablet ? (
    <div className={classNames(classes, 'white-panel')}>
      {items.map((item) => {
        const { _id, dataRoomShareableLinkConsumer, dataRoomShareableLink } =
          item;

        return (
          <div key={_id} className="waiting-list-table-wrapper__mobile-card">
            <div>
              {renderName(
                dataRoomShareableLinkConsumer.firstName,
                dataRoomShareableLinkConsumer.lastName,
                dataRoomShareableLinkConsumer.email,
              )}
            </div>
            <div className="tags">
              {dataRoomShareableLinkConsumer.companyName}
              {renderShareableLink(dataRoomShareableLink)}
            </div>
            <div className="actions">{getActions(item)}</div>
          </div>
        );
      })}
    </div>
  ) : (
    <div className="white-panel">
      <table className={classes}>
        <thead>
          <tr className="header">
            <th>{t('requestor')}</th>
            <th>{t('company')}</th>
            <th>{t('secureShare')}</th>
            <th>{actionsColumn || t('controls')}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const {
              _id,
              dataRoomShareableLinkConsumer,
              dataRoomShareableLink,
            } = item;

            return (
              <tr key={_id}>
                <td>
                  {renderName(
                    dataRoomShareableLinkConsumer.firstName,
                    dataRoomShareableLinkConsumer.lastName,
                    dataRoomShareableLinkConsumer.email,
                  )}
                </td>
                <td>{dataRoomShareableLinkConsumer.companyName || t('na')}</td>
                <td>{renderShareableLink(dataRoomShareableLink)}</td>
                <td>
                  <div className="actions">{getActions(item)}</div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default WaitingListTableWrapper;
